<!-- Wrapper element that sticks to a container edge with an optional shadow when pinned. -->

<script lang="ts" setup>
import { classProp } from '@nzyme/vue-utils/classProp.js';
import { defineProp } from '@nzyme/vue-utils/defineProp.js';
import type { Slots } from '@nzyme/vue-utils/slots.js';
import type { StickyElementContainer, StickyElementPosition } from '@nzyme/vue/useStickyElement.js';
import { useStickyElement } from '@nzyme/vue/useStickyElement.js';
import { ref } from 'vue';

const props = defineProps({
  tag: {
    type: String,
    default: 'div',
  },
  sticky: {
    type: Boolean,
    default: true,
  },
  shadowPadding: Boolean,
  shadow: {
    type: Boolean,
    default: true,
  },
  container: defineProp<StickyElementContainer>(),
  position: defineProp<StickyElementPosition>(),
  pinnedClass: classProp,
});

defineSlots<
  Slots<{
    default: {
      pinned: boolean;
    };
  }>
>();

const element = ref<HTMLElement>();
const pinned = useStickyElement({
  element: element,
  container: () => props.container,
  position: () => props.position ?? 'top',
});
</script>

<template>
  <component
    :is="tag"
    ref="element"
    :class="[sticky && css.sticky, sticky && pinned && [css.pinned, pinnedClass], css[position ?? 'top']]"
  >
    <div
      v-if="shadow"
      :class="[css.shadow, shadowPadding && css.shadowPadding]"
    />
    <div
      v-if="shadow"
      :class="css.backdrop"
    />
    <slot :pinned="pinned" />
  </component>
</template>

<style lang="scss" module="css">
.sticky {
  position: sticky;
  z-index: 2;
}

.shadow {
  height: 100%;
  position: absolute;
  left: 0;
  right: 0;
  transition: box-shadow ease-out 150ms;
  z-index: -1;
  border-radius: inherit;
}

.shadowPadding {
  left: $spacing-20;
  right: $spacing-20;
}

.backdrop {
  border-radius: inherit;
  background: inherit;
  @include absolute-fill;
  z-index: -1;
}

.top {
  top: 0;

  > .shadow {
    bottom: 0;
  }
}

.bottom {
  bottom: 0;

  > .shadow {
    top: 0;
  }
}

.pinned {
  > .shadow {
    box-shadow: $sticky-shadow;
  }
}
</style>
